import React, { useState } from 'react';

interface GalleryImage {
  name: string;
  url: string;
}

interface EventGallerySectionProps {
  images: GalleryImage[];
}

const EventGallerySection: React.FC<EventGallerySectionProps> = ({ images }) => {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openImage = (image: GalleryImage, index: number) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeImage = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    let newIndex = currentIndex;
    if (direction === 'prev') {
      newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    }
    setSelectedImage(images[newIndex]);
    setCurrentIndex(newIndex);
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-4">
          <h2 className="text-xl font-bold text-gray-800 mb-3">Event Gallery</h2>
          <div className="grid grid-cols-3 gap-2">
            {images.map((image, index) => (
              <div 
                key={index} 
                className="aspect-square cursor-pointer overflow-hidden rounded-lg"
                onClick={() => openImage(image, index)}
              >
                <img 
                  src={image.url} 
                  alt={image.name || `Gallery image ${index + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4 !m-0">
          <div className="relative w-full max-w-4xl max-h-[90vh]">
            <button 
              onClick={closeImage}
              className="absolute top-4 right-4 text-white text-3xl z-10 bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center"
            >
              &times;
            </button>
            
            <button 
              onClick={() => navigateImage('prev')}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white text-3xl z-10 bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center"
            >
              &larr;
            </button>
            
            <button 
              onClick={() => navigateImage('next')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white text-3xl z-10 bg-black bg-opacity-50 rounded-full w-10 h-10 flex items-center justify-center"
            >
              &rarr;
            </button>
            
            <img 
              src={selectedImage.url} 
              alt={selectedImage.name || 'Gallery image'}
              className="max-w-full max-h-[80vh] object-contain mx-auto"
            />
            
            <div className="text-white text-center mt-2">
              {currentIndex + 1} of {images.length}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EventGallerySection;